import React, { useEffect } from 'react';

const MetricsMonitoring = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Metrics & Monitoring - Observability for Production Systems 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master metrics and monitoring with Prometheus, Grafana, and modern observability practices. Build reliable production systems.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master metrics and monitoring with Prometheus, Grafana, and modern observability practices. Build reliable production systems.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Metrics & Monitoring: Observability for Production Systems</h1>
        <p style={styles.subtitle}>
          Build comprehensive monitoring with Prometheus, Grafana, and observability best practices
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Observability</h2>
          <p style={styles.paragraph}>
            Observability is the ability to understand the internal state of a system by examining 
            its external outputs. It encompasses metrics, logging, and distributed tracing, providing 
            comprehensive insight into system behavior, performance, and health. Effective observability 
            is crucial for maintaining reliable production systems and enabling rapid incident response.
          </p>
          <p style={styles.paragraph}>
            Modern distributed systems require sophisticated monitoring approaches that go beyond simple 
            uptime checks. Observability enables teams to understand not just what is happening, but why 
            it's happening, facilitating proactive problem resolution and continuous system improvement.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>The Three Pillars of Observability</h2>
          
          <h3 style={styles.heading}>Metrics, Logs, and Traces</h3>
          <p style={styles.paragraph}>
            The three pillars of observability work together to provide complete system visibility. 
            Understanding how to leverage each pillar effectively is essential for building robust 
            monitoring solutions.
          </p>
          <pre style={styles.codeBlock}>
{`Three Pillars of Observability:

1. METRICS - Numerical data over time
┌─────────────────────────────────────────────────────────────┐
│ What: Quantitative measurements                             │
│ When: Real-time and historical trends                       │
│ Why: Performance monitoring, alerting, capacity planning    │
│                                                             │
│ Examples:                                                   │
│ • CPU usage: 85%                                           │
│ • Request rate: 1,500 req/sec                              │
│ • Error rate: 0.5%                                         │
│ • Response time: 250ms (p95)                               │
│ • Memory usage: 2.1GB                                      │
│                                                             │
│ Characteristics:                                            │
│ ✅ Low storage overhead                                     │
│ ✅ Fast queries and aggregations                           │
│ ✅ Excellent for alerting                                  │
│ ❌ Limited context                                         │
│ ❌ Can't debug specific issues                             │
└─────────────────────────────────────────────────────────────┘

2. LOGS - Discrete events with context
┌─────────────────────────────────────────────────────────────┐
│ What: Timestamped records of events                        │
│ When: During specific operations or errors                  │
│ Why: Debugging, audit trails, detailed investigation       │
│                                                             │
│ Examples:                                                   │
│ • "User 123 logged in from IP 192.168.1.1"                │
│ • "Database connection timeout after 30s"                  │
│ • "Payment processed: $99.99 for order #456"              │
│ • "Rate limit exceeded for API key abc123"                 │
│                                                             │
│ Characteristics:                                            │
│ ✅ Rich contextual information                             │
│ ✅ Great for debugging specific issues                     │
│ ✅ Audit trails and compliance                             │
│ ❌ High storage costs                                      │
│ ❌ Difficult to aggregate                                  │
└─────────────────────────────────────────────────────────────┘

3. TRACES - Request flow through distributed systems
┌─────────────────────────────────────────────────────────────┐
│ What: End-to-end request journey                           │
│ When: Across multiple services and components              │
│ Why: Performance bottlenecks, dependency mapping           │
│                                                             │
│ Example Trace:                                              │
│ Request ID: abc123                                          │
│ ├─ API Gateway (5ms)                                       │
│ ├─ Auth Service (15ms)                                     │
│ ├─ Order Service (120ms)                                   │
│ │  ├─ Database Query (80ms)                                │
│ │  └─ Payment API (35ms)                                   │
│ └─ Response (Total: 145ms)                                 │
│                                                             │
│ Characteristics:                                            │
│ ✅ Shows service dependencies                               │
│ ✅ Identifies performance bottlenecks                      │
│ ✅ Distributed system visibility                           │
│ ❌ Complex to implement                                    │
│ ❌ High overhead if not sampled                            │
└─────────────────────────────────────────────────────────────┘

Observability Stack Integration:
┌─────────────┐    ┌─────────────┐    ┌─────────────┐
│   Metrics   │    │    Logs     │    │   Traces    │
│             │    │             │    │             │
│ Prometheus  │    │ ELK Stack   │    │   Jaeger    │
│  Grafana    │    │ Fluentd     │    │  Zipkin     │
│             │    │ Loki        │    │             │
└─────────────┘    └─────────────┘    └─────────────┘
       │                   │                   │
       └───────────────────┼───────────────────┘
                           │
                    ┌─────────────┐
                    │ Correlation │
                    │   Engine    │
                    │             │
                    │ Trace ID    │
                    │ Request ID  │
                    │ User ID     │
                    └─────────────┘

Metric Types:

Counter:
- Monotonically increasing value
- Examples: requests_total, errors_total
- Good for: rates, totals

Gauge:
- Value that can go up or down
- Examples: cpu_usage, memory_usage
- Good for: current state, levels

Histogram:
- Distribution of values in buckets
- Examples: request_duration_seconds
- Good for: percentiles, SLAs

Summary:
- Similar to histogram but pre-calculated
- Examples: response_time quantiles
- Good for: client-side calculations`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Prometheus and Grafana Stack</h2>
          
          <h3 style={styles.heading}>Prometheus Setup and Configuration</h3>
          <p style={styles.paragraph}>
            Prometheus is a powerful time-series database and monitoring system that excels at 
            collecting and storing metrics. Understanding its architecture and configuration is 
            essential for effective monitoring.
          </p>
          <pre style={styles.codeBlock}>
{`Prometheus Architecture and Setup:

# prometheus.yml - Main configuration
global:
  scrape_interval: 15s
  evaluation_interval: 15s
  external_labels:
    cluster: 'production'
    region: 'us-east-1'

rule_files:
  - "alert_rules.yml"
  - "recording_rules.yml"

alerting:
  alertmanagers:
    - static_configs:
        - targets:
          - alertmanager:9093

scrape_configs:
  # Prometheus itself
  - job_name: 'prometheus'
    static_configs:
      - targets: ['localhost:9090']
  
  # Application metrics
  - job_name: 'api-server'
    static_configs:
      - targets: ['api-server:8080']
    metrics_path: '/metrics'
    scrape_interval: 10s
    scrape_timeout: 5s
    
  # Node exporter for system metrics
  - job_name: 'node-exporter'
    static_configs:
      - targets: 
        - 'node1:9100'
        - 'node2:9100'
        - 'node3:9100'
    
  # Service discovery with Kubernetes
  - job_name: 'kubernetes-pods'
    kubernetes_sd_configs:
      - role: pod
    relabel_configs:
      - source_labels: [__meta_kubernetes_pod_annotation_prometheus_io_scrape]
        action: keep
        regex: true
      - source_labels: [__meta_kubernetes_pod_annotation_prometheus_io_path]
        action: replace
        target_label: __metrics_path__
        regex: (.+)

# Recording Rules - Pre-computed metrics
# recording_rules.yml
groups:
  - name: api_performance
    interval: 30s
    rules:
      # Request rate per service
      - record: api:request_rate_5m
        expr: |
          rate(http_requests_total[5m])
      
      # Error rate by service
      - record: api:error_rate_5m
        expr: |
          rate(http_requests_total{status=~"5.."}[5m]) /
          rate(http_requests_total[5m])
      
      # 95th percentile response time
      - record: api:response_time_p95_5m
        expr: |
          histogram_quantile(0.95, 
            rate(http_request_duration_seconds_bucket[5m])
          )
      
      # Service availability (SLI)
      - record: api:availability_5m
        expr: |
          (
            rate(http_requests_total{status!~"5.."}[5m]) /
            rate(http_requests_total[5m])
          ) * 100

# Alert Rules - Define when to alert
# alert_rules.yml
groups:
  - name: api_alerts
    rules:
      # High error rate
      - alert: HighErrorRate
        expr: api:error_rate_5m > 0.05
        for: 2m
        labels:
          severity: warning
          team: backend
        annotations:
          summary: "High error rate detected"
          description: |
            Error rate is {{ $value | humanizePercentage }} 
            for service {{ $labels.service }}
      
      # High response time
      - alert: HighResponseTime
        expr: api:response_time_p95_5m > 1.0
        for: 5m
        labels:
          severity: critical
          team: backend
        annotations:
          summary: "High response time detected"
          description: |
            95th percentile response time is {{ $value }}s 
            for service {{ $labels.service }}
      
      # Service down
      - alert: ServiceDown
        expr: up == 0
        for: 1m
        labels:
          severity: critical
          team: sre
        annotations:
          summary: "Service is down"
          description: |
            Service {{ $labels.job }} has been down for more than 1 minute

# Application Instrumentation (Python/Flask example)
from prometheus_client import Counter, Histogram, Gauge, generate_latest
from flask import Flask, request, jsonify
import time
import functools

app = Flask(__name__)

# Metrics definitions
REQUEST_COUNT = Counter(
    'http_requests_total',
    'Total HTTP requests',
    ['method', 'endpoint', 'status']
)

REQUEST_DURATION = Histogram(
    'http_request_duration_seconds',
    'HTTP request duration in seconds',
    ['method', 'endpoint']
)

ACTIVE_CONNECTIONS = Gauge(
    'active_connections',
    'Number of active connections'
)

DATABASE_CONNECTIONS = Gauge(
    'database_connections_active',
    'Number of active database connections'
)

QUEUE_SIZE = Gauge(
    'task_queue_size',
    'Number of tasks in queue',
    ['queue_name']
)

def track_requests(f):
    """Decorator to track HTTP requests"""
    @functools.wraps(f)
    def wrapper(*args, **kwargs):
        start_time = time.time()
        
        try:
            response = f(*args, **kwargs)
            status_code = getattr(response, 'status_code', 200)
            
            # Record metrics
            REQUEST_COUNT.labels(
                method=request.method,
                endpoint=request.endpoint or 'unknown',
                status=status_code
            ).inc()
            
            REQUEST_DURATION.labels(
                method=request.method,
                endpoint=request.endpoint or 'unknown'
            ).observe(time.time() - start_time)
            
            return response
            
        except Exception as e:
            # Record error metrics
            REQUEST_COUNT.labels(
                method=request.method,
                endpoint=request.endpoint or 'unknown',
                status=500
            ).inc()
            
            raise e
    
    return wrapper

@app.route('/api/users/<user_id>')
@track_requests
def get_user(user_id):
    # Simulate database call
    time.sleep(0.1)
    
    # Update gauge metrics
    DATABASE_CONNECTIONS.set(get_db_connection_count())
    
    return jsonify({'user_id': user_id, 'name': 'John Doe'})

@app.route('/api/orders', methods=['POST'])
@track_requests
def create_order():
    # Simulate processing time
    time.sleep(0.2)
    
    # Update queue size
    QUEUE_SIZE.labels(queue_name='order_processing').set(get_queue_size())
    
    return jsonify({'order_id': '12345', 'status': 'created'})

@app.route('/metrics')
def metrics():
    """Prometheus metrics endpoint"""
    return generate_latest(), 200, {'Content-Type': 'text/plain'}

@app.before_request
def before_request():
    ACTIVE_CONNECTIONS.inc()

@app.after_request
def after_request(response):
    ACTIVE_CONNECTIONS.dec()
    return response

def get_db_connection_count():
    # Return actual database connection count
    return 5

def get_queue_size():
    # Return actual queue size
    return 10

# Custom Metrics for Business Logic
ORDERS_CREATED = Counter(
    'orders_created_total',
    'Total number of orders created',
    ['product_category', 'payment_method']
)

REVENUE_TOTAL = Counter(
    'revenue_total_dollars',
    'Total revenue in dollars',
    ['product_category']
)

USER_REGISTRATIONS = Counter(
    'user_registrations_total',
    'Total user registrations',
    ['source', 'plan_type']
)

def track_business_metrics(order_data):
    """Track business-specific metrics"""
    ORDERS_CREATED.labels(
        product_category=order_data['category'],
        payment_method=order_data['payment_method']
    ).inc()
    
    REVENUE_TOTAL.labels(
        product_category=order_data['category']
    ).inc(order_data['amount'])

# Docker Compose for Prometheus Stack
# docker-compose.yml
version: '3.8'
services:
  prometheus:
    image: prom/prometheus:latest
    container_name: prometheus
    ports:
      - "9090:9090"
    volumes:
      - ./prometheus.yml:/etc/prometheus/prometheus.yml
      - ./alert_rules.yml:/etc/prometheus/alert_rules.yml
      - ./recording_rules.yml:/etc/prometheus/recording_rules.yml
      - prometheus_data:/prometheus
    command:
      - '--config.file=/etc/prometheus/prometheus.yml'
      - '--storage.tsdb.path=/prometheus'
      - '--web.console.libraries=/etc/prometheus/console_libraries'
      - '--web.console.templates=/etc/prometheus/consoles'
      - '--web.enable-lifecycle'
      - '--storage.tsdb.retention.time=30d'
    
  grafana:
    image: grafana/grafana:latest
    container_name: grafana
    ports:
      - "3000:3000"
    volumes:
      - grafana_data:/var/lib/grafana
      - ./grafana/dashboards:/etc/grafana/provisioning/dashboards
      - ./grafana/datasources:/etc/grafana/provisioning/datasources
    environment:
      - GF_SECURITY_ADMIN_PASSWORD=admin123
      - GF_USERS_ALLOW_SIGN_UP=false
    
  alertmanager:
    image: prom/alertmanager:latest
    container_name: alertmanager
    ports:
      - "9093:9093"
    volumes:
      - ./alertmanager.yml:/etc/alertmanager/alertmanager.yml
      - alertmanager_data:/alertmanager
    
  node-exporter:
    image: prom/node-exporter:latest
    container_name: node-exporter
    ports:
      - "9100:9100"
    volumes:
      - /proc:/host/proc:ro
      - /sys:/host/sys:ro
      - /:/rootfs:ro
    command:
      - '--path.procfs=/host/proc'
      - '--path.rootfs=/rootfs'
      - '--path.sysfs=/host/sys'
      - '--collector.filesystem.mount-points-exclude=^/(sys|proc|dev|host|etc)($$|/)'

volumes:
  prometheus_data:
  grafana_data:
  alertmanager_data:`}
          </pre>

          <h3 style={styles.heading}>Grafana Dashboards and Visualization</h3>
          <p style={styles.paragraph}>
            Grafana provides powerful visualization capabilities for metrics data. Creating effective 
            dashboards requires understanding different chart types, query patterns, and visualization 
            best practices.
          </p>
          <pre style={styles.codeBlock}>
{`Grafana Dashboard Configuration:

# Grafana Datasource Configuration
# grafana/datasources/prometheus.yml
apiVersion: 1

datasources:
  - name: Prometheus
    type: prometheus
    access: proxy
    url: http://prometheus:9090
    isDefault: true
    jsonData:
      timeInterval: 15s
      queryTimeout: 60s
      httpMethod: POST
    editable: true

# Dashboard JSON Configuration (simplified)
{
  "dashboard": {
    "id": null,
    "title": "API Performance Dashboard",
    "tags": ["api", "performance"],
    "timezone": "browser",
    "refresh": "30s",
    "time": {
      "from": "now-1h",
      "to": "now"
    },
    "panels": [
      {
        "id": 1,
        "title": "Request Rate",
        "type": "stat",
        "targets": [
          {
            "expr": "sum(rate(http_requests_total[5m]))",
            "legendFormat": "Requests/sec"
          }
        ],
        "fieldConfig": {
          "defaults": {
            "unit": "reqps",
            "min": 0,
            "thresholds": {
              "steps": [
                {"color": "green", "value": null},
                {"color": "yellow", "value": 100},
                {"color": "red", "value": 500}
              ]
            }
          }
        }
      },
      {
        "id": 2,
        "title": "Error Rate",
        "type": "stat",
        "targets": [
          {
            "expr": "sum(rate(http_requests_total{status=~\"5..\"}[5m])) / sum(rate(http_requests_total[5m])) * 100",
            "legendFormat": "Error %"
          }
        ],
        "fieldConfig": {
          "defaults": {
            "unit": "percent",
            "min": 0,
            "max": 100,
            "thresholds": {
              "steps": [
                {"color": "green", "value": null},
                {"color": "yellow", "value": 1},
                {"color": "red", "value": 5}
              ]
            }
          }
        }
      },
      {
        "id": 3,
        "title": "Response Time Percentiles",
        "type": "timeseries",
        "targets": [
          {
            "expr": "histogram_quantile(0.50, rate(http_request_duration_seconds_bucket[5m]))",
            "legendFormat": "50th percentile"
          },
          {
            "expr": "histogram_quantile(0.95, rate(http_request_duration_seconds_bucket[5m]))",
            "legendFormat": "95th percentile"
          },
          {
            "expr": "histogram_quantile(0.99, rate(http_request_duration_seconds_bucket[5m]))",
            "legendFormat": "99th percentile"
          }
        ],
        "fieldConfig": {
          "defaults": {
            "unit": "s",
            "min": 0
          }
        }
      },
      {
        "id": 4,
        "title": "Top Endpoints by Request Count",
        "type": "table",
        "targets": [
          {
            "expr": "topk(10, sum by (endpoint) (rate(http_requests_total[5m])))",
            "format": "table",
            "instant": true
          }
        ],
        "transformations": [
          {
            "id": "organize",
            "options": {
              "excludeByName": {"Time": true},
              "renameByName": {
                "Value": "Requests/sec",
                "endpoint": "Endpoint"
              }
            }
          }
        ]
      }
    ]
  }
}

# Advanced PromQL Queries for Dashboards

# Service Level Indicators (SLIs)
# Availability SLI
(
  sum(rate(http_requests_total{status!~"5.."}[5m])) /
  sum(rate(http_requests_total[5m]))
) * 100

# Latency SLI (95th percentile under 500ms)
histogram_quantile(0.95, 
  rate(http_request_duration_seconds_bucket[5m])
) < 0.5

# Error Budget Calculation
# Assuming 99.9% availability target (0.1% error budget)
(
  1 - (
    sum(rate(http_requests_total{status!~"5.."}[30d])) /
    sum(rate(http_requests_total[30d]))
  )
) * 100

# Resource Utilization Queries
# CPU utilization by service
avg by (service) (
  rate(cpu_usage_seconds_total[5m])
) * 100

# Memory utilization
(
  memory_usage_bytes / memory_limit_bytes
) * 100

# Disk I/O rate
rate(disk_io_bytes_total[5m])

# Network throughput
rate(network_bytes_total[5m])

# Business Metrics Queries
# Revenue per hour
increase(revenue_total_dollars[1h])

# Conversion rate
(
  sum(rate(user_registrations_total[5m])) /
  sum(rate(page_views_total{page="signup"}[5m]))
) * 100

# Active users
count(
  count by (user_id) (
    http_requests_total{endpoint="/api/user/profile"}
  )
)

# Alert Queries for Dashboard
# Services down
up == 0

# High memory usage
(memory_usage_bytes / memory_limit_bytes) > 0.8

# Database connection pool exhaustion
database_connections_active / database_connections_max > 0.9

# Queue backup
task_queue_size > 1000

# Dashboard Variables for Dynamic Filtering
# Service variable
label_values(http_requests_total, service)

# Environment variable
label_values(up, environment)

# Time range variable
$__timeFilter(timestamp)

# Dashboard Annotations
# Deployment markers
ALERTS{alertname="DeploymentStarted"}

# Incident markers
ALERTS{alertname="IncidentDeclared"}

# Maintenance windows
maintenance_window_active == 1

# Grafana Alerting Rules
{
  "alert": {
    "name": "High Error Rate",
    "message": "Error rate is above 5% for more than 2 minutes",
    "frequency": "10s",
    "conditions": [
      {
        "query": {
          "queryType": "",
          "refId": "A",
          "model": {
            "expr": "sum(rate(http_requests_total{status=~\"5..\"}[5m])) / sum(rate(http_requests_total[5m])) * 100",
            "interval": "",
            "refId": "A"
          }
        },
        "reducer": {
          "type": "last",
          "params": []
        },
        "evaluator": {
          "params": [5],
          "type": "gt"
        }
      }
    ],
    "executionErrorState": "alerting",
    "noDataState": "no_data",
    "for": "2m"
  },
  "notifications": [
    {
      "uid": "slack-alerts"
    },
    {
      "uid": "pagerduty-critical"
    }
  ]
}

# Dashboard Template Variables
{
  "templating": {
    "list": [
      {
        "name": "service",
        "type": "query",
        "query": "label_values(http_requests_total, service)",
        "refresh": 1,
        "multi": true,
        "includeAll": true
      },
      {
        "name": "environment",
        "type": "query", 
        "query": "label_values(up, environment)",
        "refresh": 1,
        "multi": false,
        "includeAll": false
      },
      {
        "name": "interval",
        "type": "interval",
        "auto": true,
        "auto_count": 30,
        "auto_min": "10s"
      }
    ]
  }
}`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Monitoring Patterns</h2>
          
          <h3 style={styles.heading}>SLI/SLO Monitoring</h3>
          <p style={styles.paragraph}>
            Service Level Indicators (SLIs) and Service Level Objectives (SLOs) provide a framework 
            for measuring and maintaining service reliability. Implementing SLI/SLO monitoring helps 
            teams focus on user-impacting metrics and make data-driven reliability decisions.
          </p>
          <pre style={styles.codeBlock}>
{`SLI/SLO Implementation:

# SLI/SLO Framework Implementation
import time
import json
from typing import Dict, List, Optional
from dataclasses import dataclass
from enum import Enum

class SLIType(Enum):
    AVAILABILITY = "availability"
    LATENCY = "latency"
    QUALITY = "quality"
    FRESHNESS = "freshness"

@dataclass
class SLI:
    name: str
    type: SLIType
    description: str
    query: str
    unit: str
    good_threshold: float
    total_threshold: Optional[float] = None

@dataclass
class SLO:
    name: str
    description: str
    target: float  # e.g., 99.9 for 99.9%
    period_days: int  # e.g., 30 for 30-day rolling window
    sli: SLI

class SLOCalculator:
    def __init__(self, prometheus_client):
        self.prometheus = prometheus_client
    
    def calculate_sli(self, sli: SLI, period_hours: int = 24) -> float:
        """Calculate SLI value for given period"""
        query = sli.query.format(period=f"{period_hours}h")
        
        try:
            result = self.prometheus.query(query)
            if result and result[0]['value']:
                return float(result[0]['value'][1])
            return 0.0
        except Exception as e:
            print(f"Error calculating SLI {sli.name}: {e}")
            return 0.0
    
    def calculate_error_budget(self, slo: SLO) -> Dict[str, float]:
        """Calculate error budget consumption"""
        period_hours = slo.period_days * 24
        current_sli = self.calculate_sli(slo.sli, period_hours)
        
        # Error budget is (100 - SLO target)
        total_error_budget = 100 - slo.target
        
        # Used error budget is (100 - current SLI)
        used_error_budget = 100 - current_sli
        
        # Remaining error budget
        remaining_error_budget = total_error_budget - used_error_budget
        
        # Error budget burn rate (how fast we're consuming it)
        current_hour_sli = self.calculate_sli(slo.sli, 1)
        hourly_error_rate = 100 - current_hour_sli
        
        # Time to exhaustion at current rate
        if hourly_error_rate > 0:
            hours_to_exhaustion = remaining_error_budget / hourly_error_rate
        else:
            hours_to_exhaustion = float('inf')
        
        return {
            'total_budget': total_error_budget,
            'used_budget': used_error_budget,
            'remaining_budget': remaining_error_budget,
            'budget_consumption_rate': (used_error_budget / total_error_budget) * 100,
            'hours_to_exhaustion': hours_to_exhaustion,
            'current_sli': current_sli
        }

# Define SLIs and SLOs
API_AVAILABILITY_SLI = SLI(
    name="api_availability",
    type=SLIType.AVAILABILITY,
    description="Percentage of successful API requests",
    query="""
    (
      sum(rate(http_requests_total{{status!~"5.."}}[{period}])) /
      sum(rate(http_requests_total[{period}]))
    ) * 100
    """,
    unit="percent",
    good_threshold=200,  # HTTP 2xx status codes
    total_threshold=None
)

API_LATENCY_SLI = SLI(
    name="api_latency",
    type=SLIType.LATENCY,
    description="Percentage of requests served under 500ms",
    query="""
    (
      sum(rate(http_request_duration_seconds_bucket{{le="0.5"}}[{period}])) /
      sum(rate(http_request_duration_seconds_count[{period}]))
    ) * 100
    """,
    unit="percent",
    good_threshold=0.5  # 500ms threshold
)

DATA_FRESHNESS_SLI = SLI(
    name="data_freshness",
    type=SLIType.FRESHNESS,
    description="Percentage of data updated within 5 minutes",
    query="""
    (
      sum(rate(data_updates_total{{age_minutes<="5"}}[{period}])) /
      sum(rate(data_updates_total[{period}]))
    ) * 100
    """,
    unit="percent",
    good_threshold=5  # 5 minutes freshness
)

# Define SLOs
API_AVAILABILITY_SLO = SLO(
    name="API Availability",
    description="API should be available 99.9% of the time",
    target=99.9,
    period_days=30,
    sli=API_AVAILABILITY_SLI
)

API_LATENCY_SLO = SLO(
    name="API Latency",
    description="95% of API requests should complete within 500ms",
    target=95.0,
    period_days=30,
    sli=API_LATENCY_SLI
)

# SLO Monitoring Dashboard
def generate_slo_dashboard(slos: List[SLO]) -> Dict:
    """Generate Grafana dashboard for SLO monitoring"""
    panels = []
    
    for i, slo in enumerate(slos):
        # SLI value panel
        panels.append({
            "id": i * 3 + 1,
            "title": f"{slo.name} - Current SLI",
            "type": "stat",
            "targets": [{
                "expr": slo.sli.query.format(period="24h"),
                "legendFormat": f"{slo.sli.name}"
            }],
            "fieldConfig": {
                "defaults": {
                    "unit": slo.sli.unit,
                    "min": 0,
                    "max": 100,
                    "thresholds": {
                        "steps": [
                            {"color": "red", "value": 0},
                            {"color": "yellow", "value": slo.target - 1},
                            {"color": "green", "value": slo.target}
                        ]
                    }
                }
            }
        })
        
        # Error budget panel
        panels.append({
            "id": i * 3 + 2,
            "title": f"{slo.name} - Error Budget",
            "type": "stat",
            "targets": [{
                "expr": f"100 - ({slo.sli.query.format(period='30d')})",
                "legendFormat": "Error Budget Used %"
            }],
            "fieldConfig": {
                "defaults": {
                    "unit": "percent",
                    "min": 0,
                    "max": 100 - slo.target,
                    "thresholds": {
                        "steps": [
                            {"color": "green", "value": 0},
                            {"color": "yellow", "value": (100 - slo.target) * 0.5},
                            {"color": "red", "value": (100 - slo.target) * 0.8}
                        ]
                    }
                }
            }
        })
        
        # SLI trend panel
        panels.append({
            "id": i * 3 + 3,
            "title": f"{slo.name} - 30-day Trend",
            "type": "timeseries",
            "targets": [
                {
                    "expr": slo.sli.query.format(period="24h"),
                    "legendFormat": "24h SLI"
                },
                {
                    "expr": str(slo.target),
                    "legendFormat": "SLO Target"
                }
            ],
            "fieldConfig": {
                "defaults": {
                    "unit": slo.sli.unit,
                    "min": slo.target - 5,
                    "max": 100
                }
            }
        })
    
    return {
        "dashboard": {
            "title": "SLO Dashboard",
            "panels": panels,
            "time": {"from": "now-30d", "to": "now"},
            "refresh": "5m"
        }
    }

# Error Budget Alerting
def create_error_budget_alerts(slos: List[SLO]) -> List[Dict]:
    """Create alerting rules for error budget consumption"""
    alerts = []
    
    for slo in slos:
        # Fast burn alert (2% budget in 1 hour)
        alerts.append({
            "alert": f"{slo.name.replace(' ', '')}FastBurn",
            "expr": f"(100 - ({slo.sli.query.format(period='1h')})) > {(100 - slo.target) * 0.02}",
            "for": "2m",
            "labels": {
                "severity": "critical",
                "slo": slo.name,
                "burn_rate": "fast"
            },
            "annotations": {
                "summary": f"Fast error budget burn for {slo.name}",
                "description": f"Error budget is being consumed at 2%/hour rate for {slo.name}"
            }
        })
        
        # Slow burn alert (10% budget in 6 hours)
        alerts.append({
            "alert": f"{slo.name.replace(' ', '')}SlowBurn",
            "expr": f"(100 - ({slo.sli.query.format(period='6h')})) > {(100 - slo.target) * 0.1}",
            "for": "15m",
            "labels": {
                "severity": "warning",
                "slo": slo.name,
                "burn_rate": "slow"
            },
            "annotations": {
                "summary": f"Slow error budget burn for {slo.name}",
                "description": f"Error budget consumption is elevated for {slo.name}"
            }
        })
    
    return alerts

# SLO Report Generation
class SLOReporter:
    def __init__(self, slo_calculator: SLOCalculator):
        self.calculator = slo_calculator
    
    def generate_monthly_report(self, slos: List[SLO]) -> Dict:
        """Generate monthly SLO compliance report"""
        report = {
            "period": "30 days",
            "generated_at": time.time(),
            "slos": []
        }
        
        for slo in slos:
            error_budget = self.calculator.calculate_error_budget(slo)
            
            slo_report = {
                "name": slo.name,
                "target": slo.target,
                "current_sli": error_budget['current_sli'],
                "compliance": error_budget['current_sli'] >= slo.target,
                "error_budget": {
                    "total": error_budget['total_budget'],
                    "used": error_budget['used_budget'],
                    "remaining": error_budget['remaining_budget'],
                    "consumption_rate": error_budget['budget_consumption_rate']
                },
                "recommendations": self._generate_recommendations(slo, error_budget)
            }
            
            report["slos"].append(slo_report)
        
        return report
    
    def _generate_recommendations(self, slo: SLO, error_budget: Dict) -> List[str]:
        """Generate recommendations based on SLO performance"""
        recommendations = []
        
        consumption_rate = error_budget['budget_consumption_rate']
        
        if consumption_rate > 80:
            recommendations.append("CRITICAL: Error budget nearly exhausted. Immediate action required.")
            recommendations.append("Consider halting non-critical deployments.")
            recommendations.append("Focus on reliability improvements.")
        
        elif consumption_rate > 50:
            recommendations.append("WARNING: High error budget consumption.")
            recommendations.append("Review recent changes and incidents.")
            recommendations.append("Consider slowing feature velocity.")
        
        elif consumption_rate < 10:
            recommendations.append("Good reliability performance.")
            recommendations.append("Consider increasing feature velocity or tightening SLO.")
        
        if error_budget['hours_to_exhaustion'] < 24:
            recommendations.append(f"Error budget will be exhausted in {error_budget['hours_to_exhaustion']:.1f} hours at current rate.")
        
        return recommendations

# Usage Example
slos = [API_AVAILABILITY_SLO, API_LATENCY_SLO]
calculator = SLOCalculator(prometheus_client)
reporter = SLOReporter(calculator)

# Generate monthly report
monthly_report = reporter.generate_monthly_report(slos)
print(json.dumps(monthly_report, indent=2))

# Generate dashboard
dashboard = generate_slo_dashboard(slos)

# Generate alerts
alerts = create_error_budget_alerts(slos)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Start with the Four Golden Signals:</strong> Monitor latency, traffic, errors, 
              and saturation as the foundation of your observability strategy.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper cardinality management:</strong> Avoid high-cardinality 
              metrics that can overwhelm your monitoring system and increase costs.
            </li>
            <li style={styles.listItem}>
              <strong>Use SLIs and SLOs:</strong> Focus on user-impacting metrics and set realistic 
              reliability targets based on business requirements.
            </li>
            <li style={styles.listItem}>
              <strong>Design for actionable alerts:</strong> Every alert should be actionable and 
              indicate a real problem that requires human intervention.
            </li>
            <li style={styles.listItem}>
              <strong>Implement comprehensive dashboards:</strong> Create role-specific dashboards 
              for different audiences (developers, operators, business stakeholders).
            </li>
            <li style={styles.listItem}>
              <strong>Plan for retention and storage:</strong> Implement appropriate retention 
              policies and consider costs when designing your metrics strategy.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Effective metrics and monitoring are essential for operating reliable production systems. 
            The combination of Prometheus for metrics collection and Grafana for visualization provides 
            a powerful foundation for observability. Implementing SLI/SLO monitoring helps teams focus 
            on user-impacting reliability metrics.
          </p>
          <p style={styles.paragraph}>
            Success with monitoring requires careful planning of what to measure, how to visualize it, 
            and when to alert. The goal is to provide actionable insights that enable teams to maintain 
            system reliability while supporting business objectives. Remember that monitoring is not 
            just about collecting data, but about turning that data into actionable intelligence.
          </p>
        </section>
      </main>
    </article>
  );
};

export default MetricsMonitoring;
